import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const trustBadges = [
  { icon: '◆', label: '25+ Apps Launched' },
  { icon: '✦', label: '95% Client Satisfaction' },
  { icon: '◇', label: '5+ Years Experience' },
];

const services = [
  {
    title: 'iOS Development',
    description: 'Native iPhone and iPad apps with polished performance and platform-perfect detail.',
    icon: 'M5 4h14v16H5V4Zm4 2v12m6-12v12',
  },
  {
    title: 'Android Development',
    description: 'Scalable Android products engineered for speed, reach, and device consistency.',
    icon: 'M6 9h12v8a3 3 0 0 1-3 3H9a3 3 0 0 1-3-3V9Zm2-5 2 3m6-3-2 3',
  },
  {
    title: 'Cross-Platform Apps',
    description: 'Unified mobile experiences that ship faster without compromising quality.',
    icon: 'M4 7h16M7 4l-3 3 3 3m10-6 3 3-3 3M8 16h8',
  },
  {
    title: 'UI/UX Design',
    description: 'Elegant interfaces, user journeys, and prototypes that make complex ideas feel simple.',
    icon: 'M5 5h6v6H5V5Zm8 0h6v6h-6V5ZM5 13h6v6H5v-6Zm8 0h6v6h-6v-6Z',
  },
  {
    title: 'App Strategy & Consulting',
    description: 'Product roadmaps, technical planning, and launch strategy for ambitious teams.',
    icon: 'M12 3l8 5-8 5-8-5 8-5Zm-6 9 6 4 6-4M6 16l6 4 6-4',
  },
  {
    title: 'Maintenance & Support',
    description: 'Continuous improvements, monitoring, and release support after launch.',
    icon: 'M12 4v4m0 8v4M4 12h4m8 0h4M6.3 6.3l2.8 2.8m5.8 5.8 2.8 2.8m0-11.4-2.8 2.8m-5.8 5.8-2.8 2.8',
  },
];

const processSteps = [
  {
    title: 'Discovery & Strategy',
    description: 'We clarify goals, user needs, and technical direction before a single screen is built.',
    icon: '◎',
  },
  {
    title: 'Design & Prototyping',
    description: 'We shape premium product flows, visual systems, and clickable prototypes fast.',
    icon: '✧',
  },
  {
    title: 'Development & Testing',
    description: 'We engineer robust app experiences with tight QA loops and launch-grade polish.',
    icon: '△',
  },
  {
    title: 'Launch & Growth',
    description: 'We support release, analytics, iteration, and the roadmap after version one.',
    icon: '↗',
  },
];

const stats = [
  { value: 25, suffix: '+', label: 'Apps Delivered' },
  { value: 50, suffix: 'K+', label: 'Users Reached' },
  { value: 95, suffix: '%', label: 'Client Satisfaction' },
  { value: 5, suffix: '+', label: 'Years of Expertise' },
];

const testimonials = [
  {
    quote:
      'Placeholder testimonial: Milestor gave our app the strategy, speed, and polish we needed to launch with confidence.',
    name: 'Ava Morgan',
    company: 'Placeholder: Nova Labs',
    initials: 'AM',
  },
  {
    quote:
      'Placeholder testimonial: The team translated a complex vision into a product our customers understood immediately.',
    name: 'Leo Carter',
    company: 'Placeholder: Orbit Health',
    initials: 'LC',
  },
  {
    quote:
      'Placeholder testimonial: Every detail felt considered, from onboarding flows to performance and release support.',
    name: 'Mina Patel',
    company: 'Placeholder: Vanta Commerce',
    initials: 'MP',
  },
];

function useCountUp(targets) {
  const statsRef = useRef(null);
  const [counts, setCounts] = useState(targets.map(() => 0));

  useEffect(() => {
    const statsElement = statsRef.current;

    if (!statsElement) {
      return undefined;
    }

    let animationFrame;
    let hasAnimated = false;

    const runAnimation = () => {
      const start = performance.now();
      const duration = 1600;

      const tick = (currentTime) => {
        const progress = Math.min((currentTime - start) / duration, 1);
        const easedProgress = 1 - Math.pow(1 - progress, 3);

        setCounts(targets.map((target) => Math.round(target.value * easedProgress)));

        if (progress < 1) {
          animationFrame = requestAnimationFrame(tick);
        }
      };

      animationFrame = requestAnimationFrame(tick);
    };

    if (!('IntersectionObserver' in window)) {
      runAnimation();
      return () => cancelAnimationFrame(animationFrame);
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          hasAnimated = true;
          runAnimation();
          observer.disconnect();
        }
      },
      { threshold: 0.35 },
    );

    observer.observe(statsElement);

    return () => {
      cancelAnimationFrame(animationFrame);
      observer.disconnect();
    };
  }, [targets]);

  return { statsRef, counts };
}

function HomePage() {
  const { statsRef, counts } = useCountUp(stats);

  return (
    <main>
      <section className="home-hero" aria-labelledby="hero-title">
        <div className="home-hero__mesh" aria-hidden="true" />
        <div className="home-hero__inner">
          <div className="home-hero__content">
            <p className="hero-chip">✦ Award-Winning App Development</p>
            <h1 id="hero-title" className="home-hero__title">
              <span>We Build Apps</span>
              <span>That Define</span>
              <span>The Future.</span>
            </h1>
            <p className="home-hero__subtitle">
              From concept to launch, Milestor crafts mobile experiences that captivate users and
              accelerate growth.
            </p>
            <div className="home-hero__actions">
              <Link className="button button--primary" to="/contact">
                Start a Project
              </Link>
              <Link className="button button--ghost" to="/services">
                View Our Work <span aria-hidden="true">-&gt;</span>
              </Link>
            </div>
            <div className="trust-badges" aria-label="Milestor trust metrics">
              {trustBadges.map((badge) => (
                <span key={badge.label}>
                  <b aria-hidden="true">{badge.icon}</b>
                  {badge.label}
                </span>
              ))}
            </div>
          </div>

          <div className="hero-visual" aria-label="Abstract mobile app preview">
            <div className="hero-visual__glow" aria-hidden="true" />
            <div className="phone-mockup">
              <div className="phone-mockup__screen">
                <div className="app-preview__top">
                  <span>Milestor OS</span>
                  <b>Live</b>
                </div>
                <div className="app-preview__card app-preview__card--main">
                  <span>Growth Score</span>
                  <strong>94%</strong>
                </div>
                <div className="app-preview__grid">
                  <span />
                  <span />
                  <span />
                  <span />
                </div>
                <div className="app-preview__bar" />
                <div className="app-preview__bar app-preview__bar--short" />
              </div>
            </div>
            <div className="floating-card floating-card--one">
              <span>Retention</span>
              <strong>+38%</strong>
            </div>
            <div className="floating-card floating-card--two">
              <span>Launch Ready</span>
              <strong>v1.0</strong>
            </div>
          </div>
        </div>
      </section>

      <section className="section section--services" id="services" aria-labelledby="services-title">
        <div className="section__heading section__heading--center reveal">
          <h2 id="services-title">What We Do</h2>
          <p>End-to-end mobile development for ambitious brands.</p>
        </div>
        <div className="services-grid">
          {services.map((service, index) => (
            <article
              className="service-card reveal"
              key={service.title}
              style={{ '--reveal-delay': `${index * 80}ms` }}
            >
              <svg className="service-card__icon" viewBox="0 0 24 24" aria-hidden="true">
                <path d={service.icon} />
              </svg>
              <h3>{service.title}</h3>
              <p>{service.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="section process-section" aria-labelledby="process-title">
        <div className="section__heading section__heading--left reveal">
          <h2 id="process-title">Our Process</h2>
          <p>A proven 4-step approach to launching exceptional apps.</p>
        </div>
        <div className="process-timeline">
          {processSteps.map((step, index) => (
            <article
              className="process-step reveal"
              key={step.title}
              style={{ '--reveal-delay': `${index * 100}ms` }}
            >
              <span className="process-step__icon" aria-hidden="true">
                {step.icon}
              </span>
              <span className="process-step__number">{String(index + 1).padStart(2, '0')}</span>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="stats-section" ref={statsRef} aria-labelledby="stats-title">
        <h2 id="stats-title" className="visually-hidden">
          Milestor social proof statistics
        </h2>
        <div className="stats-grid">
          {stats.map((stat, index) => (
            <div className="stat-item" key={stat.label}>
              <strong>
                {counts[index]}
                {stat.suffix}
              </strong>
              <span>{stat.label}</span>
            </div>
          ))}
        </div>
        <figure className="stats-quote reveal">
          <blockquote>“Milestor turned our idea into a product our users love.”</blockquote>
          <figcaption>Placeholder: Jordan Ellis, Founder at Northstar Apps</figcaption>
        </figure>
      </section>

      <section className="section testimonials-section" aria-labelledby="testimonials-title">
        <div className="section__heading section__heading--center reveal">
          <h2 id="testimonials-title">What Our Clients Say</h2>
          <p>Placeholder feedback from ambitious product teams.</p>
        </div>
        <div className="testimonial-grid">
          {testimonials.map((testimonial, index) => (
            <article
              className="testimonial-card reveal"
              key={testimonial.name}
              style={{ '--reveal-delay': `${index * 100}ms` }}
            >
              <span className="testimonial-card__quote" aria-hidden="true">
                “
              </span>
              <div className="testimonial-card__stars" aria-label="5 out of 5 stars">
                ★★★★★
              </div>
              <p>{testimonial.quote}</p>
              <div className="testimonial-card__client">
                <span>{testimonial.initials}</span>
                <div>
                  <strong>{testimonial.name}</strong>
                  <small>{testimonial.company}</small>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="cta-banner" aria-labelledby="cta-title">
        <div className="cta-banner__inner reveal">
          <h2 id="cta-title">Ready to Build Something Extraordinary?</h2>
          <p>Let's turn your vision into an app that users will love.</p>
          <div className="cta-banner__actions">
            <Link className="button button--light" to="/contact">
              Start Your Project
            </Link>
            <Link className="button button--ghost" to="/contact">
              Schedule a Free Call
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}

export default HomePage;
